import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import classNames from 'classnames';
import { NotificationBlock } from 'components/ui';
import { CommentIcon } from 'components/ui/icons/CommentIcon';
import { ProfileIcon } from 'components/ui/icons/ProfileIcon';
import dayjs from 'dayjs';
import { PlaceChainTypeEnum } from 'types/Place';
import datetime from 'utils/datetime';

import { cancelBooking, fetchBookingByGuid } from '../../../../api/booking';
import useBookingRootLink from '../../../../hooks/useBookingRootLink';
import useBookingStorage from '../../../../providers/BookingStorage/hooks';
import { EventSourceType, useEventSource } from '../../../../providers/EventSource/hook';
import i18n from '../../../../providers/Locale';
import usePlace from '../../../../providers/Place/hooks';
import ComponentType from '../../../../types/Component';
import Translation from '../../../../types/Translation';
import { BookingStatus, BookingStatusESMap, BookingType } from '../../../../types/booking';
import WarningBlock from '../../../WarningBlock';
import Button from '../../../ui/Button';
import Dialog from '../../../ui/Dialog';
import LoadingOverlay from '../../../ui/LoadingOverlay';
import BookingCancelled from '../../../ui/icons/BookingCancelled';
import Clock2 from '../../../ui/icons/Clock2';
import SadIcon from '../../../ui/icons/Sad';
import SpinnerColorDots from '../../../ui/icons/SpinnerColorDots';
import TransactionIcon from '../../../ui/icons/Transaction';

import styles from './style.module.scss';

const CANCELLED_BY_CLIENT = 'CANCELLED_BY_CLIENT';

type StatusPropsType = {
	outsideWorkingMessage: string | JSX.Element;
	showWorkingHoursMessage: boolean;
};

const Status: ComponentType<StatusPropsType> = (statusData) => {
	const { outsideWorkingMessage, showWorkingHoursMessage } = statusData;
	const { guid } = useParams();
	const router = useNavigate();
	const bookingRootLink = useBookingRootLink();
	const { place } = usePlace();
	const [booking, setBooking] = useState<BookingType>();
	const [error, setError] = useState<string>();
	const [eventSourceToken, setEventSourceToken] = useState<string>();
	const [isConfirmDialog, setIsConfirmDialog] = useState<boolean>(false);
	const [isLoadingCancellation, setIsLoadingCancellation] = useState<boolean>(false);
	const customEventSource = useEventSource();
	const dateObj = useMemo(() => {
		if (!booking?.dateTime) return null;

		return dayjs(booking.dateTime).toDate();
	}, [booking]);
	const time = useMemo(() => {
		if (!dateObj) return null;

		return dayjs(dateObj).format('HH:mm');
	}, [dateObj]);
	const bookingStorage = useBookingStorage();

	const isCancelledByClient = useMemo(() => {
		return booking?.cancelReason === CANCELLED_BY_CLIENT;
	}, [booking]);

	const subscribeOnEventSource = (token: string) => {
		customEventSource.subscribe(EventSourceType.BOOKING, token);
		customEventSource.onMessage(token, (data) => {
			if (data.booking && data.step) {
				setBooking((prevState) => {
					if (!prevState) return undefined;

					const newState = {
						...prevState,
						status: BookingStatusESMap[data.step!] || prevState.status,
						cancelReason: data.booking?.cancelReason || prevState.cancelReason,
					};

					bookingStorage.dispatchUpdateAction(newState);

					return newState;
				});
			}
		});
	};

	const fetchBookingData = async () => {
		const { result, error } = await fetchBookingByGuid(guid as string);

		if (error) {
			return setError(i18n(error.message as keyof Translation) || i18n('unexpected_error'));
		}

		if (result) {
			setBooking(result);
			setEventSourceToken(result.token);

			if (result.status === BookingStatus.CREATED) {
				subscribeOnEventSource(result.token);
			} else if (
				result.status === BookingStatus.COMPLETED ||
				result.status === BookingStatus.CONFIRMED ||
				result.status === BookingStatus.CANCELLED ||
				result.status === BookingStatus.EXTERNAL_CANCELLING ||
				result.status === BookingStatus.EXPIRED
			) {
				bookingStorage.dispatchDeleteAction(result.guid);
			}
		}
	};

	const onErrorDialogClose = () => {
		router(bookingRootLink);
	};

	const statusIcon = useMemo(() => {
		if (!booking) return null;

		switch (booking.status) {
			case BookingStatus.CREATED:
				return (
					<div style={{ margin: 'auto' }}>
						<SpinnerColorDots />
					</div>
				);
			case BookingStatus.CONFIRMED:
				return (
					<div className={styles.statusSuccess}>
						<Clock2 size={48} />
					</div>
				);
			case BookingStatus.CANCELLED:
				return (
					<>
						{isCancelledByClient && (
							<div className={styles.statusCancel}>
								<BookingCancelled />
							</div>
						)}
					</>
				);
			default:
				return (
					<div className={styles.statusCancel}>
						<SadIcon size={48} />
					</div>
				);
		}
	}, [booking, isCancelledByClient]);

	const getTitle = useMemo(() => {
		switch (booking?.status) {
			case BookingStatus.CREATED:
				return i18n('booking_request_sent');
			case BookingStatus.CONFIRMED:
				return i18n('booking_request_confirmed');
			default:
				return null;
		}
	}, [booking]);

	const customerInfo = useMemo(() => {
		if (!booking) return null;
		return [booking.customer.name, booking.customer.phone, booking.customer.email]
			.filter((i) => !!i)
			.join(', ');
	}, [booking]);

	const getDescription = useMemo(() => {
		switch (booking?.status) {
			case BookingStatus.CREATED:
				return i18n('booking_we_will_check', undefined, true);
			case BookingStatus.CONFIRMED:
				return i18n('booking_we_are_waiting');
			case BookingStatus.CANCELLED:
				return (
					<>
						{isCancelledByClient && (
							<div className={styles.descriptionCancel}>{i18n('booking_cancelled_by_client')}</div>
						)}
					</>
				);
			default:
				return <div className={styles.descriptionCancel}>{i18n('booking_we_are_sorry')}</div>;
		}
	}, [booking, isCancelledByClient]);

	const onBtnCancelClick = useCallback(() => {
		setIsConfirmDialog(true);
	}, []);

	const onDialogClose = useCallback(() => {
		setIsConfirmDialog(false);
	}, []);

	const onConfirmCancellation = useCallback(async () => {
		setIsLoadingCancellation(true);

		try {
			const { result, error } = await cancelBooking(guid as string);

			if (result && booking) {
				setBooking({
					...booking,
					status: BookingStatus.CANCELLED,
					cancelReason: CANCELLED_BY_CLIENT,
					cancellable: false,
				});
			} else if (error) {
				setError(i18n(error.message as keyof Translation) || i18n('unexpected_error'));
			}
		} catch (e) {
			console.error(e);
		}

		setIsLoadingCancellation(false);
		setIsConfirmDialog(false);
	}, [booking]);

	useEffect(() => {
		if (!booking || booking.guid !== guid) {
			fetchBookingData();
		}

		return () => {
			customEventSource.closeConnection(eventSourceToken!);
		};
	}, [guid]);

	const showStatuses =
		!place?.customize.reservationHideWaitingSpinner || booking?.status !== BookingStatus.CREATED;

	if (error) {
		return (
			<Dialog visible={!!error} title={i18n('error')} onClose={onErrorDialogClose}>
				<div className={styles.errorDialog}>
					<div className={styles.errorDialogText}>{error}</div>
					<Button appearance={'primary'} onClick={onErrorDialogClose}>
						{i18n('ok')}
					</Button>
				</div>
			</Dialog>
		);
	}

	if (!booking) {
		return <LoadingOverlay />;
	}

	return (
		<div className={classNames(styles.root, !showStatuses && styles.noLoader)}>
			{showStatuses && (
				<>
					<div className={styles.statusIcon}>{statusIcon}</div>
					<div className={styles.title}>{getTitle}</div>
					<div className={styles.description}>
						{showWorkingHoursMessage &&
						!place?.opened &&
						![BookingStatus.CANCELLED, BookingStatus.EXTERNAL_CANCELLING].includes(booking?.status)
							? outsideWorkingMessage
							: getDescription}
					</div>
				</>
			)}
			{booking.deposit && booking.status === BookingStatus.CREATED && (
				<NotificationBlock className={classNames(styles.warning, styles.paidStatus)}>
					{i18n('booking.paid.status_msg', undefined, true, { isSpan: true })}
				</NotificationBlock>
			)}
			{booking?.cancelReason && !isCancelledByClient && (
				<NotificationBlock className={classNames(styles.warning, styles.cancelReason)}>
					<b>
						{i18n('booking.cancelReason.comment')}
						{':'}
					</b>
					<span>{booking?.cancelReason}</span>
				</NotificationBlock>
			)}
			{booking.deposit && booking.status === BookingStatus.CANCELLED && (
				<NotificationBlock className={styles.warning}>
					{<span>{i18n('booking.paid.cancel_msg', {}, true)}</span>}
				</NotificationBlock>
			)}
			<div className={classNames(styles.info, booking?.cancelReason && styles.isCancel)}>
				<div className={styles.infoBlock}>
					<h5>{dateObj && datetime.format.dayLabel.getMonthDayMinimal(dateObj)}</h5>
					<p>{dateObj && datetime.format.dayLabel.getWeekDayName(dateObj)}</p>
				</div>
				<div className={styles.infoBlock}>
					<h5>{time}</h5>
					<p>{`${booking.personCount} ${i18n(booking.personCount > 1 ? 'persons' : 'person')}`}</p>
				</div>
			</div>
			{place && place.contactInfo.address && place.placeType === PlaceChainTypeEnum.ChainBranch && (
				<div className={classNames(styles.address, booking?.cancelReason && styles.isCancel)}>
					<h5>{place.name}</h5>
					<p>{place.contactInfo.address.prediction}</p>
				</div>
			)}
			{[BookingStatus.CREATED, BookingStatus.CONFIRMED].includes(booking.status) && (
				<div className={styles.cancelBtn}>
					<Button
						fullWidth
						appearance="danger"
						size="large"
						disabled={!booking.cancellable}
						onClick={onBtnCancelClick}>
						<div className={styles.cancelBtnText}>{i18n('cancel_reservation')}</div>
						{booking.deposit && (
							<>
								{booking.cancellable ? (
									<div className={styles.cancelBtnDescription}>
										{i18n('booking.cancel.no_later_24_hours')}
									</div>
								) : (
									<div className={styles.cancelBtnDescription}>
										{i18n('booking.cancel.contact_us')}
									</div>
								)}
							</>
						)}
					</Button>
				</div>
			)}
			{!!place?.contactInfo?.phone &&
				[BookingStatus.CONFIRMED, BookingStatus.CREATED].includes(booking.status) && (
					<div className={styles.cancel}>
						{i18n(
							'booking_to_cancel_text',
							{
								phone: `<a href="tel:${place.contactInfo.phone}">${place.contactInfo.phone}</a>`,
							},
							true
						)}
					</div>
				)}
			<div className={styles.customerInfo}>
				{booking.comment && (
					<div className={styles.customerBlock}>
						<div className={styles.infoIcon}>
							<CommentIcon />
						</div>
						<div>
							<h6>{i18n('comment_to_reservation')}</h6>
							<p>{booking.comment}</p>
						</div>
					</div>
				)}
				{customerInfo && (
					<div className={styles.customerBlock}>
						<div className={styles.infoIcon}>
							<ProfileIcon />
						</div>
						<div>
							<h6>{i18n('your_contact_data')}</h6>
							<p>{customerInfo}</p>
						</div>
					</div>
				)}
			</div>
			<Dialog width={382} visible={isConfirmDialog} onClose={onDialogClose}>
				<div className={styles.confirmDialog}>
					<div className={styles.confirmDialogText}>
						{i18n('booking.cancel.confirm_please', undefined, true, { isSpan: true })}
					</div>
					{booking.deposit && (
						<div className={styles.confirmDialogWarning}>
							<WarningBlock icon={<TransactionIcon />}>
								<span style={{ fontWeight: 700 }}>{i18n('booking.deposit_will_returned')}</span>
							</WarningBlock>
						</div>
					)}
					<div className={styles.confirmDialogBtn}>
						<Button
							fullWidth
							loading={isLoadingCancellation}
							size="large"
							appearance="danger"
							onClick={onConfirmCancellation}>
							{i18n('booking.cancel.confirm')}
						</Button>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default Status;
